// Client-specific Firebase collection helpers
import { collection, doc, CollectionReference, DocumentReference } from 'firebase/firestore';
import { db } from './firebase';
import type { 
  Gallery, 
  Artist, 
  Collection, 
  CollectionPieceMembership,
  GalleryUser, 
  GalleryDomain,
  PieceStats, 
  PieceView, 
  UserSession, 
  AnalyticsCache, 
  AnalyticsHistory, 
  Favorite, 
  Piece,
  Frame,
  FrameFamily,
  UserFramePreferences 
} from '@shared/types/firestore';
import {
  COLLECTIONS,
  getAnalyticsCachesPath,
  getGalleryAnalyticsHistoryPath,
  getArtistPath,
  getArtistsPath,
  getCollectionPiecesPath,
  getCollectionPath,
  getCollectionsPath,
  getGalleryFavoritePiecePath,
  getGalleryFavoritePiecesPath,
  getGalleryDomainPath,
  getFrameFamiliesPath,
  getFrameFamilyPath,
  getFramePath,
  getFramesPath,
  getGalleryPath,
  getGalleryPieceStatsPath,
  getPiecePath,
  getPiecesPath,
  getUserFramePreferencesDocumentPath,
  getUserFramePreferencesPath,
  getUserPath,
  getUsersPath,
  getUserSessionsPath
} from '@shared/constants/firebase-paths';

// Helper functions
function createCollection<T>(path: string) {
  return collection(db, path) as CollectionReference<T>;
}

function createDoc<T>(path: string) {
  return doc(db, path) as DocumentReference<T>;
}

// Gallery collections
export const getGalleriesCollection = () => createCollection<Gallery>(COLLECTIONS.GALLERIES);
export const getGalleryDoc = (galleryId: string) => createDoc<Gallery>(getGalleryPath(galleryId));
export const getGalleryDomainDoc = (subdomain: string) =>
  createDoc<GalleryDomain>(getGalleryDomainPath(subdomain));

// Collection-related collections  
export const getCollectionsCollection = (galleryId: string) => 
  createCollection<Collection>(getCollectionsPath(galleryId));

export const getCollectionDoc = (galleryId: string, collectionId: string) =>
  createDoc<Collection>(getCollectionPath(galleryId, collectionId));

export const getCollectionPiecesCollection = (galleryId: string, collectionId: string) =>
  createCollection<CollectionPieceMembership>(getCollectionPiecesPath(galleryId, collectionId));

// Artist collections
export const getArtistsCollection = (galleryId: string) =>
  createCollection<Artist>(getArtistsPath(galleryId));

export const getArtistDoc = (galleryId: string, artistId: string) =>
  createDoc<Artist>(getArtistPath(galleryId, artistId));

// Piece collections
export const getPiecesCollection = (galleryId: string) =>
  createCollection<Piece>(getPiecesPath(galleryId));

export const getPieceDoc = (galleryId: string, pieceId: string) =>
  createDoc<Piece>(getPiecePath(galleryId, pieceId));

// User collections
export const getUsersCollection = (galleryId: string) =>
  createCollection<GalleryUser>(getUsersPath(galleryId));

export const getUserDoc = (galleryId: string, userId: string) =>
  createDoc<GalleryUser>(getUserPath(galleryId, userId));

// Analytics collections
export const getUserSessionsCollection = (galleryId: string) =>
  createCollection<UserSession>(getUserSessionsPath(galleryId));

export const getPieceViewsCollection = () =>
  createCollection<PieceView>(COLLECTIONS.VIEWING_EVENTS);

export const getPieceStatsCollection = (galleryId: string) =>
  createCollection<PieceStats>(getGalleryPieceStatsPath(galleryId));

// Favorites collections  
export const getFavoritesCollection = (galleryId: string, userId: string) =>
  createCollection<Favorite>(getGalleryFavoritePiecesPath(galleryId, userId));

export const getFavoriteDoc = (galleryId: string, userId: string, pieceId: string) =>
  createDoc<Favorite>(getGalleryFavoritePiecePath(galleryId, userId, pieceId));

// Cache and history collections
export const getAnalyticsCacheCollection = (galleryId: string) =>
  createCollection<AnalyticsCache>(getAnalyticsCachesPath(galleryId));

export const getAnalyticsHistoryCollection = (galleryId: string) =>
  createCollection<AnalyticsHistory>(getGalleryAnalyticsHistoryPath(galleryId));

// Frame Family collections
export const getFrameFamiliesCollection = (galleryId: string) =>
  createCollection<FrameFamily>(getFrameFamiliesPath(galleryId));

export const getFrameFamilyDoc = (galleryId: string, familyId: string) =>
  createDoc<FrameFamily>(getFrameFamilyPath(galleryId, familyId));

// Frame collections (nested under families)
export const getFramesCollection = (galleryId: string, familyId: string) =>
  createCollection<Frame>(getFramesPath(galleryId, familyId));

export const getFrameDoc = (galleryId: string, familyId: string, frameId: string) =>
  createDoc<Frame>(getFramePath(galleryId, familyId, frameId));

// Helper to get all frames across all families
export const getAllFramesQuery = (galleryId: string) => {
  // This will require a collection group query in the service
  return `galleries/${galleryId}/frameFamilies/*/frames`;
};

// User frame preferences collections
export const getUserFramePreferencesCollection = (galleryId: string) =>
  createCollection<UserFramePreferences>(getUserFramePreferencesPath(galleryId));

export const getUserFramePreferencesDoc = (galleryId: string, userId: string) =>
  createDoc<UserFramePreferences>(getUserFramePreferencesDocumentPath(galleryId, userId));
